// @flow
import * as React from 'react';
import { getDistance, convertDistance } from 'geolib';
import { Element, scroller } from 'react-scroll';
import type { VenueListProps, CenterType } from '../types';

import './index.scss';

const VenueList = ({
  myPosition,
  venues,
  activeCenter,
  setActiveCenter,
}: VenueListProps) => {
  const activeCenterHash: string | null = activeCenter
    ? `${activeCenter.lat}${activeCenter.lng}`
    : null;

  const onListItemClick = center => {
    const mapElement = document.getElementById('map');
    const isMobile = window.innerWidth <= 991;

    setActiveCenter(center);

    if (mapElement && isMobile) {
      if ('scrollBehavior' in document.documentElement.style) {
        mapElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        mapElement.scrollIntoView();
      }
    }
  };

  const getMeasureUnit = () => {
    const language =
      'language' in navigator
        ? navigator.language
        : 'userLanguage' in navigator
        ? navigator.userLanguage
        : 'en';
    return language === 'en-US' ? 'mi' : 'km';
  };

  const computeDistance = (myPosition, venue) => {
    const unit = getMeasureUnit();
    const distance = Math.round(
      convertDistance(getDistance(myPosition, venue), unit),
    );

    return `${distance} ${unit}`;
  };

  React.useEffect(() => {
    const venuesList = document.getElementById('venues-list');
    if (!venuesList || !('scrollTo' in venuesList)) return;
    if ('scrollBehavior' in document.documentElement.style)
      venuesList.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    else venuesList.scrollTo(0, 0);
  }, [venues]);

  return (
    <Element className="venues-list" id="venues-list">
      <div classname="venue-list-legend">
        <span className="venue-distance-legend">
          <i class="fas fa-location"></i> 123 {getMeasureUnit()}
        </span>
        &nbsp;: distance from your current location, based on browser
        geolocation (if provided)
      </div>
      <ul className="collection-items">
        {venues.map(venue => {
          const venueHash: string = `${venue.latitude}${venue.longitude}`;
          const isActiveCenter: boolean = venueHash === activeCenterHash;
          const newCenter: CenterType = {
            lat: venue.latitude,
            lng: venue.longitude,
          };
          return (
            <li
              className={`venue${isActiveCenter ? ' active-center' : ''}${
                venue.category ? ' has-categories' : ''
              }`}
              key={venueHash}
            >
              <Element name={venueHash}>
                <h3 className="venue-title">
                  <button onClick={() => onListItemClick(newCenter)}>
                    <span>{venue.title}</span>
                  </button>
                </h3>
                <p>
                  {/* We need to put ZIP before Country, description field has correct positioning */}
                  {/* but is it always complete? Checked 50 objs, seems good enough. Old ref: */}
                  {/* {venue.address_street} &mdash; {venue.address_city} */}
                  {venue.description}
                </p>
                {myPosition && (
                  <p className="venue-distance">
                    <i class="fas fa-location"></i> &nbsp;
                    {computeDistance(myPosition, venue)}
                  </p>
                )}
                <div className="external-links">
                  <a
                    href={`http://maps.google.com/maps?q=${venue.title}%20${venue.address_street},${venue.address_city}`}
                    title="Go to Google Maps"
                    className="gmaps-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i class="fas fa-map-marked-alt"></i>
                    <span>Directions</span>
                  </a>
                  {venue.website && (
                    <a
                      className="website-link"
                      href={venue.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Visit Website
                    </a>
                  )}
                  {venue.phone && (
                    <a className="telephone-link" href={`tel:${venue.phone}`}>
                      {venue.phone}
                    </a>
                  )}
                </div>
                {venue.category && (
                  <div className="venue-category">
                    <span
                      className={`category${
                        venue.category.toLowerCase() === 'new' ? ' new' : ''
                      }`}
                    >
                      {venue.category}
                    </span>
                  </div>
                )}
              </Element>
            </li>
          );
        })}
      </ul>
    </Element>
  );
};

export default React.memo<VenueListProps>(VenueList);
